/**
 * TableCard — Individual table card for the discovery grid.
 *
 * Rich design:
 *  • Severity-accented left border + subtle header gradient
 *  • Prominent readiness score with label
 *  • Metadata chips (cols, rows, issues) with icons
 *  • Inline quality dimensions mini-bar when provided
 *  • Issue preview in-card (top 3) with severity icons
 *  • Compact variant for dense list view
 */

import { AlertTriangle, Columns3, Rows3, ShieldCheck } from "lucide-react";
import type { SampleCap } from "../../api/profiler-api";
import type { SeverityLevel } from "./ScoreHero";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TableCardState = "default" | "selected" | "error";

export interface TableIssue {
  message: string;
  severity: SeverityLevel;
}

export interface TableCardProps {
  tableName: string;
  columnCount: number;
  rowCount: number;
  /** Sample size actually profiled. When provided and < rowCount, the card
   *  shows a small "Profiled N of M (X%)" hint so users can tell their
   *  results came from a sample, not the full table. */
  sampleRowCount?: number;
  /** Set when the per-table size budget (not just the row limit) cut the
   *  sample short. Shown as a "Size-capped" badge so a profile resting on
   *  fewer rows than requested is never mistaken for a normal sample. */
  sampleCap?: SampleCap;
  score: number;
  severity: SeverityLevel;
  issues?: TableIssue[];
  state?: TableCardState;
  compact?: boolean;
  onClick?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}

// ---------------------------------------------------------------------------
// Severity presentation
// ---------------------------------------------------------------------------

// The single source of truth for every tier's colour is the
// --readiness-<tier> CSS variable (ztd-design-system.css, theme-aware). The
// accent bar + header tint derive from it via inline styles below, instead
// of the previous hardcoded Tailwind palette names (good used lime/emerald
// GREEN while --readiness-good is BLUE — the reported "Good is blue in the
// donut but green in Tables" mismatch). `cssVar` names the token per tier.
const SEVERITY_BADGE: Record<
  SeverityLevel,
  {
    icon: string;
    bg: string;
    text: string;
    label: string;
    cssVar: string;
  }
> = {
  excellent: {
    icon: "■",
    bg: "bg-[var(--readiness-excellent)]/15",
    text: "text-[var(--readiness-excellent)]",
    label: "Excellent",
    cssVar: "--readiness-excellent",
  },
  good: {
    icon: "✓",
    bg: "bg-[var(--readiness-good)]/15",
    text: "text-[var(--readiness-good)]",
    label: "Good",
    cssVar: "--readiness-good",
  },
  moderate: {
    icon: "▲",
    bg: "bg-[var(--readiness-moderate)]/15",
    text: "text-[var(--readiness-moderate)]",
    label: "Moderate",
    cssVar: "--readiness-moderate",
  },
  warning: {
    icon: "▲",
    bg: "bg-[var(--readiness-warning)]/15",
    text: "text-[var(--readiness-warning)]",
    label: "Warning",
    cssVar: "--readiness-warning",
  },
  poor: {
    icon: "◆",
    bg: "bg-[var(--readiness-poor)]/15",
    text: "text-[var(--readiness-poor)]",
    label: "Poor",
    cssVar: "--readiness-poor",
  },
  critical: {
    icon: "●",
    bg: "bg-[var(--readiness-critical)]/15",
    text: "text-[var(--readiness-critical)]",
    label: "Critical",
    cssVar: "--readiness-critical",
  },
};

function formatRowCount(n: number): string {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** Tooltip for the size-capped badge: says what happened and why it matters. */
export function sampleCapTitle(cap: SampleCap): string {
  return (
    `This table is wide or holds very large values, so sampling stopped at ` +
    `${cap.rows_kept.toLocaleString()} of the ${cap.rows_requested.toLocaleString()} rows requested ` +
    `to stay within the per-table memory limit. Statistics such as null %, distinct counts and key ` +
    `candidates are based on the smaller sample.`
  );
}

export function TableCard({
  tableName,
  columnCount,
  rowCount,
  sampleRowCount,
  sampleCap,
  score,
  severity,
  issues = [],
  state = "default",
  compact = false,
  onClick,
  onKeyDown,
}: TableCardProps) {
  const isSampled =
    typeof sampleRowCount === "number" &&
    sampleRowCount > 0 &&
    sampleRowCount < rowCount;
  const samplePct = isSampled
    ? Math.max(0.001, (sampleRowCount! / rowCount) * 100)
    : 0;
  const badge = SEVERITY_BADGE[severity] || SEVERITY_BADGE.good;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick?.();
    }
    onKeyDown?.(e);
  };

  const selectedStyles =
    state === "selected"
      ? "ring-2 ring-[var(--primary)] shadow-[0_0_24px_rgba(134,188,37,0.25)]"
      : "ring-1 ring-white/10 hover:ring-white/25";
  const errorStyles =
    state === "error" ? "border-l-4 border-l-[var(--destructive)]" : "";

  // The card root is a generic <div role="button">, not an <article>: ARIA
  // forbids overriding a sectioning element's implicit role with a widget
  // role, and role="button" makes children presentational — so
  // <article role="button"> both failed validation and hid the card's content
  // from AT. The whole card is one actuator with a complete aria-label, and
  // CardGrid already supplies the role="listitem" wrapper. See #490.
  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={state === "selected"}
      aria-label={`${tableName}: score ${Math.round(score)} ${badge.label}, ${columnCount} columns, ${formatRowCount(rowCount)} rows, ${issues.length} issues`}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      className={`
        group relative rounded-xl glass-surface cursor-pointer overflow-hidden
        transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl
        focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ring)]
        ${selectedStyles} ${errorStyles}
        ${compact ? "min-h-[100px]" : "min-h-[200px]"}
        flex flex-col
      `}
    >
      {/* Severity-accented left border — colour from the tier's readiness var */}
      <span
        aria-hidden="true"
        className="absolute left-0 top-0 bottom-0 w-1"
        style={{
          borderLeftWidth: 4,
          borderLeftStyle: "solid",
          borderLeftColor: `var(${badge.cssVar})`,
        }}
      />

      {/* Header — name + prominent score. Tint is a subtle wash of the same
          readiness var (color-mix keeps it faint) so it can't diverge from
          the badge/accent colour. */}
      <div
        className="px-4 pt-3.5 pb-3 border-b border-white/5"
        style={{
          backgroundImage: `linear-gradient(to bottom right, color-mix(in srgb, var(${badge.cssVar}) 10%, transparent), transparent)`,
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-sm font-semibold text-white/95 truncate flex-1 font-mono tracking-tight leading-snug">
            {tableName}
          </h3>
          <div className={`shrink-0 flex flex-col items-end ${badge.text}`}>
            <span className="text-lg font-bold leading-none tabular-nums">
              {Math.round(score)}
            </span>
            <span className="text-[11px] uppercase tracking-widest opacity-85 mt-0.5">
              {badge.icon} {badge.label}
            </span>
          </div>
        </div>

        {/* Metadata chips */}
        <div className="mt-2.5 flex items-center gap-3 text-[11px] text-white/55">
          <span
            className="inline-flex items-center gap-1"
            title={`${columnCount.toLocaleString()} columns`}
          >
            <Columns3 className="w-3 h-3 opacity-60" />
            <span className="tabular-nums">{columnCount}</span>
            <span className="text-white/35">cols</span>
          </span>
          <span
            className="inline-flex items-center gap-1"
            title={
              isSampled
                ? `Profiled ${sampleRowCount!.toLocaleString()} of ${rowCount.toLocaleString()} rows (${samplePct.toFixed(samplePct < 1 ? 2 : 1)}% sample)`
                : `${rowCount.toLocaleString()} rows`
            }
          >
            <Rows3 className="w-3 h-3 opacity-60" />
            <span className="tabular-nums">{formatRowCount(rowCount)}</span>
            <span className="text-white/35">rows</span>
          </span>
          <span
            className={`ml-auto inline-flex items-center gap-1 ${
              issues.length > 0 ? "text-white/75" : "text-white/40"
            }`}
            title={`${issues.length} quality issue${issues.length === 1 ? "" : "s"}`}
          >
            {issues.length === 0 ? (
              <ShieldCheck className="w-3 h-3 text-emerald-400/80" />
            ) : (
              <AlertTriangle className="w-3 h-3 text-amber-400/80" />
            )}
            <span className="tabular-nums">{issues.length}</span>
            <span className="text-white/35">
              issue{issues.length === 1 ? "" : "s"}
            </span>
          </span>
        </div>
        {sampleCap && (
          <p
            data-testid="sample-capped"
            className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-300/90"
            title={sampleCapTitle(sampleCap)}
          >
            <AlertTriangle className="w-3 h-3" aria-hidden="true" />
            Size-capped sample: {sampleCap.rows_kept.toLocaleString()} of{" "}
            {sampleCap.rows_requested.toLocaleString()} rows
          </p>
        )}
        {isSampled && !compact && !sampleCap && (
          <p
            className="mt-1 text-[11px] text-white/45"
            title={`${sampleRowCount!.toLocaleString()} of ${rowCount.toLocaleString()} rows`}
          >
            Profiled {formatRowCount(sampleRowCount!)} of{" "}
            {formatRowCount(rowCount)} (
            {samplePct.toFixed(samplePct < 1 ? 2 : 1)}%)
          </p>
        )}
      </div>

      {/* Body — issue preview (standard variant) */}
      {!compact && issues.length > 0 && (
        <ul className="mt-auto px-4 pt-3 pb-3 space-y-1.5">
          {issues.slice(0, 3).map((issue, i) => {
            const ib = SEVERITY_BADGE[issue.severity] || SEVERITY_BADGE.warning;
            return (
              <li key={i} className="flex items-start gap-1.5">
                <span
                  className={`shrink-0 text-xs ${ib.text} mt-0.5`}
                  aria-hidden="true"
                >
                  {ib.icon}
                </span>
                <p className="text-[11px] text-white/75 truncate leading-snug">
                  {issue.message}
                </p>
              </li>
            );
          })}
          {issues.length > 3 && (
            <li className="text-[11px] text-white/40 pl-5 italic">
              +{issues.length - 3} more — click to view all
            </li>
          )}
        </ul>
      )}

      {!compact && issues.length === 0 && (
        <div className="mt-auto px-4 pt-3 pb-3">
          <p className="text-[11px] text-emerald-300/80 flex items-center gap-1.5">
            <ShieldCheck className="w-3 h-3" />
            No quality issues detected
          </p>
        </div>
      )}

      {/* Compact variant — single summary line */}
      {compact && (
        <p className="px-4 pt-2 pb-3 text-[11px] text-white/55 truncate">
          {issues.length === 0
            ? "No issues — looks clean"
            : `${issues.length} issue${issues.length !== 1 ? "s" : ""}`}
        </p>
      )}
    </div>
  );
}
